import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AdminPanel } from "../components/index.js";
import { useApiClient } from "../api/ApiContext.jsx";
import { useApi } from "../hooks/useApi.js";
import { useDebounced } from "../hooks/useDebounced.js";
import { AsyncBoundary, ErrorView, InlineMessage, PageLoading, errorText } from "../ui/Feedback.jsx";

/**
 * Route is already behind <RequireAdmin/>; this screen only supplies data.
 * The customer drawer exists because AdminPanel's `onOpenCustomer` and the
 * suspend/resume endpoints have no dedicated component in the hand-off set.
 */
export default function AdminScreen() {
  const api = useApiClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search, 300);
  const [openCustomerId, setOpenCustomerId] = useState(null);
  const [message, setMessage] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // The list is paged at the server, so the operator can grow the window rather
  // than being told about customers they cannot see. The API caps a page at 200.
  const [limit, setLimit] = useState(50);

  const metricsQuery = useApi(() => api.admin.metrics(), [api, refreshKey]);
  const customersQuery = useApi(
    () => api.admin.customers(debouncedSearch, { limit }),
    [api, debouncedSearch, limit, refreshKey],
  );
  const auditQuery = useApi(() => api.admin.audit(20), [api, refreshKey]);
  const detailQuery = useApi(
    () => (openCustomerId ? api.admin.customer(openCustomerId) : Promise.resolve(null)),
    [api, openCustomerId, refreshKey],
    { skip: !openCustomerId },
  );

  function refreshAll() {
    setRefreshKey((key) => key + 1);
  }

  async function toggleSuspend(customer) {
    setMessage(null);
    try {
      const suspended = customer.status === "suspended";
      if (suspended) await api.admin.resume(customer.id);
      else await api.admin.suspend(customer.id);
      setMessage({
        tone: "success",
        text: suspended ? `${customer.account} resumed.` : `${customer.account} suspended.`,
      });
      refreshAll();
    } catch (err) {
      setMessage({ tone: "danger", text: errorText(err, "Could not change that customer's status.") });
    }
  }

  async function deleteCustomer(customer) {
    // Deletion takes the projects, calendars and billing history with it, so the
    // confirmation names the consequence rather than asking "are you sure".
    const confirmed = window.confirm(
      `Delete ${customer.account} and everything they own? Their projects, calendars and billing history are removed and this cannot be undone.`,
    );
    if (!confirmed) return;
    setMessage(null);
    try {
      await api.admin.remove(customer.id);
      setOpenCustomerId(null);
      setMessage({ tone: "success", text: `${customer.account} deleted.` });
      refreshAll();
    } catch (err) {
      setMessage({ tone: "danger", text: errorText(err, "Could not delete that customer.") });
    }
  }

  if (metricsQuery.loading || customersQuery.loading) return <PageLoading />;

  const failed = metricsQuery.error || customersQuery.error;
  if (failed) {
    return (
      <div>
        <InlineMessage tone="danger">{errorText(failed)}</InlineMessage>
        <ErrorView
          error={failed}
          onRetry={() => {
            metricsQuery.reload();
            customersQuery.reload();
          }}
          onBack={() => navigate("/")}
        />
      </div>
    );
  }

  const customers = customersQuery.data.customers;
  const openCustomer = customers.find((customer) => customer.id === openCustomerId) ?? null;

  return (
    <div>
      {message ? <InlineMessage tone={message.tone}>{message.text}</InlineMessage> : null}

      {/* Reliability is a screen rather than a block here: it is read during an
          incident, when the customer list is noise. */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <Link className="ical-btn" to="/admin/reliability" style={{ textDecoration: "none" }}>
          Reliability
        </Link>
      </div>

      <AdminPanel
        metrics={metricsQuery.data}
        customers={customers}
        onSearch={setSearch}
        onOpenCustomer={(customerId) =>
          setOpenCustomerId((current) => (current === customerId ? null : customerId))
        }
      />

      {customers.length > 0 ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginTop: 10,
          }}
        >
          <span style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
            Showing {customers.length} of {customersQuery.data.total}
          </span>
          {customersQuery.data.hasMore ? (
            <button
              type="button"
              className="ical-btn"
              // The API caps a page at 200; past that the search box is the way
              // to narrow the list rather than a longer scroll.
              disabled={customers.length >= 200}
              onClick={() => setLimit((current) => Math.min(current + 50, 200))}
            >
              {customers.length >= 200 ? "Narrow the search to see more" : "Load more"}
            </button>
          ) : null}
        </div>
      ) : null}

      {openCustomer ? (
        <div className="ical-card" style={{ marginTop: 16 }}>
          {detailQuery.loading ? <PageLoading label="Loading customer…" /> : null}
          {detailQuery.error ? (
            <InlineMessage tone="danger">{errorText(detailQuery.error)}</InlineMessage>
          ) : null}
          {!detailQuery.loading && !detailQuery.error ? (
            <>
              <p style={{ margin: "0 0 6px", fontSize: "var(--text-base)", fontWeight: 500 }}>
                {openCustomer.account}
              </p>
              <p style={{ margin: "0 0 10px", fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
                {detailQuery.data?.customer?.project_count ?? openCustomer.projectCount ?? 0} projects ·{" "}
                {detailQuery.data?.customer?.feed_count ?? openCustomer.feedCount ?? 0} calendars
              </p>
              <pre
                style={{
                  background: "var(--surface-0)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius)",
                  padding: 12,
                  fontSize: "var(--text-xs)",
                  fontFamily: "var(--font-mono)",
                  overflowX: "auto",
                  margin: "0 0 12px",
                }}
              >
                {JSON.stringify(detailQuery.data, null, 2)}
              </pre>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="ical-btn"
                  onClick={() => toggleSuspend(openCustomer)}
                >
                  {openCustomer.status === "suspended" ? "Resume account" : "Suspend account"}
                </button>
                <button
                  type="button"
                  className="ical-btn ical-btn-danger"
                  onClick={() => deleteCustomer(openCustomer)}
                >
                  Delete account
                </button>
                <button type="button" className="ical-btn" onClick={() => setOpenCustomerId(null)}>
                  Close
                </button>
              </div>
            </>
          ) : null}
        </div>
      ) : null}

      <div style={{ marginTop: 24 }}>
        <p style={{ margin: "0 0 8px", fontSize: "var(--text-base)", fontWeight: 500 }}>
          Recent admin actions
        </p>
        {auditQuery.loading ? <PageLoading label="Loading audit log…" /> : null}
        {auditQuery.error ? (
          <InlineMessage tone="warning">
            Audit log unavailable: {errorText(auditQuery.error)}
          </InlineMessage>
        ) : null}
        {!auditQuery.loading && !auditQuery.error ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {auditQuery.data.entries.length === 0 ? (
              <p style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", margin: 0 }}>
                No recorded admin actions.
              </p>
            ) : null}
            {auditQuery.data.entries.map((entry, index) => (
              <div
                key={`${entry.at}-${index}`}
                className="ical-row"
                style={{ display: "grid", gridTemplateColumns: "160px 140px 1fr", gap: 12, fontSize: "var(--text-sm)" }}
              >
                <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-secondary)" }}>
                  {entry.at}
                </span>
                <span>{entry.actor}</span>
                <span>
                  {entry.action} → {entry.target} {entry.detail ? `(${entry.detail})` : ""}
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
